import React from 'react';
import { Icon } from './AdminShell';
import { AreaChart, Spark } from './charts';
import {
  dispatchAgo, dispatchCopy, dispatchDuration, dispatchNumber, isArabic
} from './dispatchCopy';

/*
 * Layar Dispatch.
 *
 * DIRANCANG UNTUK DATA YANG SEPI. Prototipe client menampilkan AED 18.420 hari
 * ini dan 74 booking; produksi punya 114 booking sepanjang masa, nol hari ini,
 * dan completed terakhir 43 hari lalu. Konsekuensinya di sini:
 *
 *   - Rentang bawaan 30 hari, bukan "Today". Tidak ada hari dengan transaksi,
 *     dan layar yang selalu dibuka kosong akan dianggap rusak.
 *   - Ada rentang "All time". Di situ baru ada isinya.
 *   - Tiap kartu kosong menyebut KAPAN terakhir ada aktivitas, bukan sekadar
 *     menampilkan 0. "Belum ada sejak 30 hari" itu informasi; "0" itu ambigu.
 *
 * Tata letaknya mengikuti prototipe client: rentang dan Export di kanan judul,
 * Revenue lebar (g-hero), empat ubin bersparkline, lalu pipeline dan antrean
 * Needs attention.
 *
 * Daftar provider online tetap ada, tapi dipindah ke DALAM kartu Fleet status
 * alih-alih menempati slot Needs attention. Dashboard sekarang punya daftar itu
 * dan dispatcher memakainya untuk memilih siapa yang ditugaskan; membuangnya
 * demi tata letak berarti mengambil sesuatu yang sudah dipakai.
 */

export const RANGES = [['today', 'Today'], ['7d', '7 days'], ['30d', '30 days'], ['all', 'All time']];

const STATUS_COLOR = {
  completed: 'var(--ok)', expired: 'var(--tx-3)', refunded: 'var(--info)',
  cancelledByUser: 'var(--danger)', cancelledByPartner: 'var(--danger)',
  cancelledByAdmin: 'var(--danger)'
};

const fmt = (n, locale) => dispatchNumber(Math.round(n), locale);

// Persen perubahan. Naik dari nol tidak punya persentase yang berarti — "naik
// tak hingga" bukan angka yang bisa dibaca — jadi dikembalikan null dan UI
// menampilkan teks "baru" alih-alih panah.
const pct = m => {
  if (!m || m.prev === null || m.prev === undefined) return null;
  if (m.prev === 0) return null;
  return ((m.value - m.prev) / m.prev) * 100;
};

const Delta = ({ metric, locale }) => {
  const p = pct(metric);
  if (p === null) {
    if (metric && metric.prev === 0 && metric.value > 0) {
      return <span className="delta up">{dispatchCopy(locale).newest}</span>;
    }
    return null;
  }
  const up = p >= 0;
  return (
    <span className={`delta ${up ? 'up' : 'down'}`}>
      {up ? '▲' : '▼'} {dispatchNumber(Math.abs(p), locale,
        { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%
    </span>
  );
};

const Tile = ({ label, value, metric, note, series, seriesKey, locale }) => (
  <div className="tile">
    <div className="k">{label}</div>
    <div className="row">
      <div className="v num">{value}</div>
      <Delta metric={metric} locale={locale} />
    </div>
    {series && series.length > 0 && (
      <div className="spark"><Spark series={series} valueKey={seriesKey} /></div>
    )}
    {note && <div className="note">{note}</div>}
  </div>
);

/*
 * Donat armada. Digambar dari dua busur, bukan pustaka grafik: satu nilai
 * sebagai bagian dari satu total tidak membutuhkan 40 KB dependensi.
 */
const Donut = ({ onDuty, total, label, locale }) => {
  const R = 52, C = 2 * Math.PI * R;
  const frac = total > 0 ? onDuty / total : 0;
  return (
    <svg viewBox="0 0 140 140" style={{ width: '100%', maxWidth: 190, display: 'block', margin: '0 auto' }}>
      <circle cx="70" cy="70" r={R} fill="none" stroke="var(--surface-3)" strokeWidth="14" />
      <circle cx="70" cy="70" r={R} fill="none" stroke="var(--brand-amber)" strokeWidth="14"
        strokeDasharray={`${(C * frac).toFixed(1)} ${C.toFixed(1)}`}
        strokeLinecap="round" transform="rotate(-90 70 70)" />
      <text x="70" y="68" textAnchor="middle" fill="var(--tx)"
        style={{ fontSize: 30, fontWeight: 700 }}>{fmt(onDuty, locale)}</text>
      <text x="70" y="86" textAnchor="middle" fill="var(--tx-3)"
        style={{ fontSize: 10, letterSpacing: '.12em' }}>{label}</text>
    </svg>
  );
};

const Empty = ({ children }) => (
  <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--tx-3)', padding: '6px 0' }}>{children}</p>
);

class Dispatch extends React.Component {
  state = { dismissed: false };

  render() {
    const { data, providers, range, onRange, loading, locale = 'en-US' } = this.props;
    const d = data || {};
    const t = dispatchCopy(locale);
    const ar = isArabic(locale);
    const num = value => fmt(value, locale);
    const ago = days => dispatchAgo(days, locale);
    const duration = secs => dispatchDuration(secs, locale);
    const cur = d.currency || 'AED';
    const online = (providers && providers.summary && providers.summary.online) || 0;
    const offline = (providers && providers.summary && providers.summary.offline) || 0;
    const list = (providers && providers.result) || [];
    // Sama dengan definisi status pada halaman Providers.
    const busy = list.filter(p => p.isActive && p.activeStatus === 'active').length;
    const attentionRows = (d.pipeline || [])
      .filter(r => r.key === 'created' || r.key === 'reviewed')
      .map(r => r.key === 'created'
        ? { ...r, value: Math.max(0, r.value - (d.unassignedDirect || 0)) }
        : r)
      .filter(r => r.value > 0);
    const attentionCount = (d.unassignedDirect || 0)
      + attentionRows.reduce((total, row) => total + row.value, 0);

    return (
      <div dir={ar ? 'rtl' : 'ltr'}>
        {/* Banner hanya muncul kalau memang ada yang menunggu. Banner "0 item"
            melatih orang untuk mengabaikan banner. */}
        {d.unassignedDirect > 0 && !this.state.dismissed && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              <b>{t.directPaid(d.unassignedDirect, num(d.unassignedDirect))}</b>
              {/* Umurnya yang membuat ini mendesak: "2 menunggu" bisa berarti
                  dua menit atau dua hari. */}
              {d.oldestUnassignedMins != null &&
                <em> {t.oldestWaiting(duration(d.oldestUnassignedMins * 60))}</em>}
            </span>
            <a className="btn sm" href="/admin/direct-bookings">{t.openQueue}</a>
            {/* Dismiss hanya menyembunyikan untuk kunjungan ini — tidak
                menandai apa pun sebagai selesai. Muncul lagi saat dimuat ulang,
                karena bookingnya memang masih menunggu. */}
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ dismissed: true })}>{t.dismiss}</button>
          </div>
        )}

        <div className="grid g-hero" style={{ marginBottom: 'var(--sp-4)' }}>
          <div className="card">
            <div className="card-h"><h2>{t.revenue}</h2>
              <div className="sp"><span className="tag amber">{t.ranges[range]}</span></div>
            </div>
            <div className="card-b">
              <div className="big-fig">
                <div className="lbl">{t.grossBookings}</div>
                <div className="revenue-value-row">
                  <div className="val num">
                    <span className="cur">{cur} </span>
                    <span>{loading ? '—' : num((d.gross || {}).value || 0)}</span>
                  </div>
                  <Delta metric={d.gross} locale={locale} />
                </div>
              </div>
              {(d.gross || {}).prev !== null && (d.gross || {}).prev !== undefined && (
                <div className="revenue-compare">
                  {t.versus} <b>{cur} {num((d.gross || {}).prev)}</b> {t.previousPeriod}
                </div>
              )}

              {!loading && (d.gross || {}).value === 0 && (
                <Empty>{t.noCompleted(ago(d.lastCompletedDaysAgo))}</Empty>
              )}

              {(d.series || []).length > 0 && (
                <div className="revenue-chart">
                  <AreaChart series={d.series} valueKey="gross" prefix={cur + ' '}
                    locale={locale} summary={t.chartSummary} />
                </div>
              )}

              <div className="revenue-stats">
                <div>{t.avgJobValue}<b>{cur} {num((d.avgJobValue || {}).value || 0)}</b></div>
                <div>{t.completed}<b>{num((d.completed || {}).value || 0)}</b></div>
                <div>{t.allTime}<b>{cur} {num(d.allTimeGross || 0)}</b></div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h2>{t.fleetStatus}</h2>
              <div className="sp"><span className="tag ok"><i />{t.live}</span></div>
            </div>
            <div className="card-b">
              <Donut onDuty={online} total={online + offline} label={t.online} locale={locale} />
              <div className="legend" style={{ marginTop: 'var(--sp-4)' }}>
                <button type="button" onClick={() => { window.location.href = '/siteadmin/partners'; }}>
                  <span className="sw" style={{ background: 'var(--brand-amber)' }} />
                  <span className="nm">{t.onJob}</span><span className="ct">{num(busy)}</span>
                </button>
                <button type="button" onClick={() => { window.location.href = '/siteadmin/partners'; }}>
                  <span className="sw" style={{ background: 'var(--ok)' }} />
                  <span className="nm">{t.idle}</span><span className="ct">{num(online - busy)}</span>
                </button>
                <button type="button" onClick={() => { window.location.href = '/siteadmin/partners'; }}>
                  <span className="sw" style={{ background: 'var(--tx-3)' }} />
                  <span className="nm">{t.offline}</span><span className="ct">{num(offline)}</span>
                </button>
              </div>

              {/* Nama, bukan cuma angka. Dispatcher yang diminta menugaskan
                  pekerjaan harus melihat siapa yang tersedia tanpa pindah
                  halaman — dashboard sekarang punya daftar ini dan dipakai. */}
              {/* Online now sementara disembunyikan. */}
              {false && list.length > 0 && (
                <div style={{ marginTop: 'var(--sp-4)' }}>
                  <div className="pipe-grp">{t.online}</div>
                  {list.slice(0, 5).map(p => (
                    <div key={p.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '5px 0', fontSize: 'var(--fs-sm)'
                    }}>
                      <span style={{
                        width: 7, height: 7, borderRadius: '50%', flex: 'none',
                        background: p.isActive && p.activeStatus === 'active' ? 'var(--brand-amber)' : 'var(--ok)'
                      }} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {p.name || 'Unnamed'}
                      </span>
                      <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                        {p.activeService || p.location || '—'}
                      </span>
                    </div>
                  ))}
                  {online > 5 && (
                    <a className="btn ghost sm" style={{ marginTop: 8 }} href="/siteadmin/partners">
                      {t.online} {num(online)} <Icon name="chevR" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid g-4" style={{ marginBottom: 'var(--sp-4)' }}>
          <Tile label={t.bookings} value={loading ? '—' : num((d.bookings || {}).value || 0)}
            metric={d.bookings} series={d.series} seriesKey="bookings"
            note={t.lastBooking(ago(d.lastBookingDaysAgo))} locale={locale} />
          <Tile label={t.completed} value={loading ? '—' : num((d.completed || {}).value || 0)}
            metric={d.completed} series={d.series} seriesKey="completed"
            note={t.lastCompleted(ago(d.lastCompletedDaysAgo))} locale={locale} />
          <Tile label={t.newCustomers} value={loading ? '—' : num((d.newCustomers || {}).value || 0)}
            metric={d.newCustomers} series={d.series} seriesKey="customers" locale={locale} />
          <Tile label={t.newProviders} value={loading ? '—' : num((d.newProviders || {}).value || 0)}
            metric={d.newProviders} series={d.series} seriesKey="providers"
            note={t.registered(num(online + offline))} locale={locale} />
        </div>

        <div className="grid g-duo">
          <div className="card">
            <div className="card-h"><h2>{t.jobPipeline}</h2>
              <div className="sp"><span className="tag">{t.now}</span></div>
            </div>
            <div className="card-b">
              <div className="pipe">
                <div className="pipe-grp">{t.activeNow}</div>
                {(d.pipeline || []).length === 0
                  ? <Empty>{t.nothingInFlight(ago(d.lastBookingDaysAgo))}</Empty>
                  : this.renderRows(d.pipeline, t, locale)}
                <div className="pipe-grp">{t.outcomes}</div>
                {(d.outcomes || []).length === 0
                  ? <Empty>{t.noClosed}</Empty>
                  : this.renderRows(d.outcomes, t, locale)}
              </div>

              {/* Median, bukan rata-rata. Satu booking uji yang disetujui
                  berhari-hari kemudian menggeser rata-rata sendirian; pada data
                  ini rata-ratanya 4 jam sementara mediannya 21 detik. */}
              <div className="foot-stats">
                <div>{t.medianAccept}<b>{duration(d.medianAcceptSecs)}</b></div>
                <div>{t.medianArrival}<b>{duration(d.medianArrivalSecs)}</b></div>
                <div>{t.cancellationRate}<b>
                  {d.cancelRate === null || d.cancelRate === undefined ? '—' : `${dispatchNumber(d.cancelRate, locale,
                    { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
                </b></div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-h"><h2>{t.needsAttention}</h2>
              <div className="sp">
                <span className="tag">{attentionCount}</span>
              </div>
            </div>
            <div className="card-b flush">
              {/*
                * Antrean nyata, bukan contoh. Prototipe client mencampur
                * beberapa jenis peringatan yang datanya belum ada; yang benar-
                * benar bisa dideteksi sekarang ada dua:
                *   1. direct booking sudah dibayar tapi belum ada providernya
                *   2. pekerjaan terbuka yang masih menunggu diterima/ditinjau
                */}
              {attentionCount === 0
                ? <div className="card-b"><Empty>{t.nothingNeeds(ago(d.lastBookingDaysAgo))}</Empty></div>
                : (
                  <div>
                    {(d.unassignedDirect || 0) > 0 && (
                      <div className="q-item">
                        <span className="q-sev" style={{ background: 'var(--danger)' }} />
                        <span className="q-main">
                          <b>{t.directUnassigned(d.unassignedDirect, num(d.unassignedDirect))}</b>
                          <span className="muted" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>
                            {t.paidWaiting}
                            {d.oldestUnassignedMins != null && ` · ${t.oldest(duration(d.oldestUnassignedMins * 60))}`}
                          </span>
                        </span>
                        <a className="btn sm" href="/admin/direct-bookings">{t.assign}</a>
                      </div>
                    )}
                    {attentionRows.map(r => (
                      <div className="q-item" key={r.key}>
                        <span className="q-sev" style={{ background: 'var(--warn)' }} />
                        <span className="q-main">
                          <b>{t.jobsStatus(r.value, num(r.value), t.status[r.key] || r.key)}</b>
                          <span className="muted" style={{ display: 'block', fontSize: 'var(--fs-xs)' }}>
                            {t.stillOpen}
                          </span>
                        </span>
                        <a className="btn sm ghost" href="/siteadmin/jobs">{t.open}</a>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderRows(rows, t, locale) {
    const max = Math.max.apply(null, rows.map(r => r.value).concat([1]));
    return rows.map(r => (
      <div className="pipe-row" key={r.key}>
        <span className="nm">
          <i style={{ background: STATUS_COLOR[r.key] || 'var(--brand-amber)' }} />
          {t.status[r.key] || r.key}
        </span>
        <span className="bar">
          <span style={{ width: `${(r.value / max) * 100}%`, background: STATUS_COLOR[r.key] || 'var(--brand-amber)' }} />
        </span>
        <span className="ct num">{fmt(r.value, locale)}</span>
      </div>
    ));
  }
}

export default Dispatch;
